import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { px } from "@/components/tool-kit/charts/marks";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_PADDING,
  CHART_WIDTH,
  LINE_HEIGHT,
  layoutCategoryLabels,
  layoutTitle,
} from "@/lib/tool-kit/charts/layout";
import {
  estimateTextWidth,
  maxTextWidth,
  truncateText,
} from "@/lib/tool-kit/charts/text";
import {
  type ColorBy,
  colorAt,
  type HeatmapScale,
  scaleFraction,
} from "@/lib/tool-kit/color-charts/heatmap";
import type { HeatmapPalette } from "@/lib/tool-kit/color-charts/heatmap-palettes";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A heatmap drawn as SVG: a grid of coloured cells with row names down the left, column
 * names along the top and a colour bar on the right.
 *
 * Cell width is whatever the 640-unit image leaves once the names and the colour bar have
 * their room (never more than 90), cell height follows from it, and the image grows with
 * the number of rows — so a 40-row table stays readable instead of being squeezed.
 */

const TICK = CHART_FONT_SIZE.tick;
const VALUE_SIZE = CHART_FONT_SIZE.value;
const LINE = TICK * LINE_HEIGHT;
const MAX_CELL_WIDTH = 90;
const MAX_ROW_LABEL = 150;
const LEGEND_GAP = 22;
const BAR_WIDTH = 14;
const BAR_STEPS = 48;
const ROTATE_45 = Math.SQRT1_2;
const MISSING_FILL = "#f2f2f2";
const MISSING_LINE = "#cfcfcf";
const AXIS_BAND = CHART_FONT_SIZE.axisTitle * LINE_HEIGHT + 8;

/** Whether `text` fits a cell `width` wide with a little air either side. */
export function fitsCell(text: string, width: number): boolean {
  return estimateTextWidth(text, { fontSize: VALUE_SIZE }) <= width - 6;
}

export interface HeatmapSizes {
  tick: (value: number) => string;
  rowTexts: string[];
  leftWidth: number;
  legendWidth: number;
  yBand: number;
  cellWidth: number;
  cellHeight: number;
}

/** Cell size and the room the names and colour bar take; shared by the chart and widget. */
export function heatmapSizes(input: {
  rowLabels: string[];
  columns: number;
  scale: HeatmapScale;
  by: ColorBy;
  fmt: NumberFormat;
  highText: string;
  lowText: string;
  hasYTitle: boolean;
}): HeatmapSizes {
  const { scale, by, fmt } = input;
  const tick = tickFormat(fmt, scale.decimals);
  const rowTexts = input.rowLabels.map((text) =>
    truncateText(text, MAX_ROW_LABEL, { fontSize: TICK })
  );
  const leftWidth = maxTextWidth(rowTexts, { fontSize: TICK });
  const legendTexts =
    by === "table" ? scale.ticks.map(tick) : [input.highText, input.lowText];
  const legendWidth =
    BAR_WIDTH + 6 + 3 + maxTextWidth(legendTexts, { fontSize: VALUE_SIZE });
  const yBand = input.hasYTitle ? AXIS_BAND : 0;
  const available =
    CHART_WIDTH -
    2 * CHART_PADDING -
    yBand -
    leftWidth -
    8 -
    LEGEND_GAP -
    legendWidth;
  const cellWidth = Math.max(
    8,
    Math.min(MAX_CELL_WIDTH, available / Math.max(1, input.columns))
  );
  const cellHeight = Math.max(24, Math.min(40, cellWidth * 0.6));
  return {
    tick,
    rowTexts,
    leftWidth,
    legendWidth,
    yBand,
    cellWidth,
    cellHeight,
  };
}

function ColorBar({
  x,
  y,
  height,
  palette,
  reverse,
  scale,
  by,
  tick,
  highText,
  lowText,
}: {
  x: number;
  y: number;
  height: number;
  palette: HeatmapPalette;
  reverse: boolean;
  scale: HeatmapScale;
  by: ColorBy;
  tick: (value: number) => string;
  highText: string;
  lowText: string;
}) {
  const step = height / BAR_STEPS;
  const textX = x + BAR_WIDTH + 6;
  const span = scale.high - scale.low;
  return (
    <g>
      {Array.from({ length: BAR_STEPS }, (_, index) => {
        const along = 1 - (index + 0.5) / BAR_STEPS;
        const t =
          by === "table"
            ? scaleFraction(scale.low + along * span, scale)
            : along;
        return (
          <rect
            fill={colorAt(palette, t, reverse)}
            height={px(step + 0.6)}
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed ramp of steps
            key={index}
            width={BAR_WIDTH}
            x={px(x)}
            y={px(y + index * step)}
          />
        );
      })}
      {by === "table" ? (
        scale.ticks.map((value) => {
          const at = y + height - ((value - scale.low) / span) * height;
          return (
            <g key={value}>
              <line
                stroke={CHART_INK.text}
                strokeWidth={1}
                x1={px(x + BAR_WIDTH)}
                x2={px(x + BAR_WIDTH + 3)}
                y1={px(at)}
                y2={px(at)}
              />
              <text
                fill={CHART_INK.muted}
                fontSize={VALUE_SIZE}
                x={px(textX + 3)}
                y={px(at + VALUE_SIZE * 0.35)}
              >
                {tick(value)}
              </text>
            </g>
          );
        })
      ) : (
        <g>
          <text
            fill={CHART_INK.muted}
            fontSize={VALUE_SIZE}
            x={px(textX)}
            y={px(y + VALUE_SIZE * 0.8)}
          >
            {highText}
          </text>
          <text
            fill={CHART_INK.muted}
            fontSize={VALUE_SIZE}
            x={px(textX)}
            y={px(y + height)}
          >
            {lowText}
          </text>
        </g>
      )}
    </g>
  );
}

export function HeatmapChart({
  id,
  rowLabels,
  columnLabels,
  grid,
  fractions,
  palette,
  reverse,
  scale,
  by,
  showValues,
  style,
  fmt,
  label,
  description,
  highText,
  lowText,
}: {
  id: string;
  rowLabels: string[];
  columnLabels: string[];
  grid: (number | null)[][];
  /** Ramp position of every cell (null for a missing one), from `cellFractions`. */
  fractions: (number | null)[][];
  palette: HeatmapPalette;
  reverse: boolean;
  scale: HeatmapScale;
  by: ColorBy;
  showValues: boolean;
  /** `xTitle` names the columns, `yTitle` the rows. */
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
  /** Colour-bar ends for row and column colouring. */
  highText: string;
  lowText: string;
}) {
  const rows = rowLabels.length;
  const columns = columnLabels.length;
  const yTitle = style.yTitle.trim();
  const xTitle = style.xTitle.trim();
  const {
    tick,
    rowTexts,
    leftWidth,
    legendWidth,
    yBand,
    cellWidth,
    cellHeight,
  } = heatmapSizes({
    rowLabels,
    columns,
    scale,
    by,
    fmt,
    highText,
    lowText,
    hasYTitle: yTitle !== "",
  });
  const plotWidth = columns * cellWidth;
  const plotHeight = rows * cellHeight;
  const labels = layoutCategoryLabels(columnLabels, cellWidth);
  const widestRotated = labels.rotated
    ? maxTextWidth(
        labels.lines.map((lines) => lines[0] ?? ""),
        { fontSize: TICK }
      )
    : 0;
  const rotatedReach = labels.rotated
    ? Math.max(
        0,
        widestRotated * ROTATE_45 - cellWidth / 2 - LEGEND_GAP - legendWidth
      )
    : 0;
  const content =
    yBand + leftWidth + 8 + plotWidth + LEGEND_GAP + legendWidth + rotatedReach;
  const left = Math.max(CHART_PADDING, (CHART_WIDTH - content) / 2);
  const plotX = left + yBand + leftWidth + 8;

  const title = layoutTitle(style.apa ? "" : style.title, CHART_WIDTH);
  let top = CHART_PADDING + (title?.height ?? 0);
  const xTitleY = top + CHART_FONT_SIZE.axisTitle;
  if (xTitle) {
    top += AXIS_BAND;
  }
  const plotY = top + labels.height + 6;
  const barHeight = Math.min(220, Math.max(90, plotHeight));
  const barY = plotY + Math.max(0, (plotHeight - barHeight) / 2);
  const height = Math.ceil(
    Math.max(plotY + plotHeight, barY + barHeight) + CHART_PADDING
  );
  const barX = plotX + plotWidth + LEGEND_GAP;
  const fillAt = (fraction: number) => colorAt(palette, fraction, reverse);

  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={CHART_WIDTH}
    >
      <ChartTitle title={title} />
      {xTitle ? (
        <text
          fill={CHART_INK.text}
          fontSize={CHART_FONT_SIZE.axisTitle}
          textAnchor="middle"
          x={px(plotX + plotWidth / 2)}
          y={px(xTitleY)}
        >
          {truncateText(xTitle, plotWidth, {
            fontSize: CHART_FONT_SIZE.axisTitle,
          })}
        </text>
      ) : null}
      {yTitle ? (
        <text
          fill={CHART_INK.text}
          fontSize={CHART_FONT_SIZE.axisTitle}
          textAnchor="middle"
          transform={`rotate(-90 ${px(left + CHART_FONT_SIZE.axisTitle)} ${px(plotY + plotHeight / 2)})`}
          x={px(left + CHART_FONT_SIZE.axisTitle)}
          y={px(plotY + plotHeight / 2)}
        >
          {truncateText(yTitle, plotHeight, {
            fontSize: CHART_FONT_SIZE.axisTitle,
          })}
        </text>
      ) : null}
      <g>
        {columnLabels.map((name, column) => {
          const centre = plotX + (column + 0.5) * cellWidth;
          const lines = labels.lines[column] ?? [name];
          if (labels.rotated) {
            return (
              <text
                fill={CHART_INK.text}
                fontSize={TICK}
                // biome-ignore lint/suspicious/noArrayIndexKey: positional column labels, names may repeat
                key={`c-${name}-${column}`}
                transform={`rotate(-45 ${px(centre)} ${px(plotY - 6)})`}
                x={px(centre)}
                y={px(plotY - 6)}
              >
                {lines[0]}
              </text>
            );
          }
          return (
            <text
              fill={CHART_INK.text}
              fontSize={TICK}
              // biome-ignore lint/suspicious/noArrayIndexKey: positional column labels, names may repeat
              key={`c-${name}-${column}`}
              textAnchor="middle"
            >
              {lines.map((line, row) => (
                <tspan
                  key={line}
                  x={px(centre)}
                  y={px(plotY - 6 - (lines.length - 1 - row) * LINE)}
                >
                  {line}
                </tspan>
              ))}
            </text>
          );
        })}
        {rowTexts.map((text, row) => (
          <text
            fill={CHART_INK.text}
            fontSize={TICK}
            key={`r-${rowLabels[row]}-${row}`}
            textAnchor="end"
            x={px(plotX - 6)}
            y={px(plotY + (row + 0.5) * cellHeight + TICK * 0.35)}
          >
            {text}
          </text>
        ))}
      </g>
      <g>
        {fractions.map((cells, row) =>
          cells.map((fraction, column) => {
            const x = plotX + column * cellWidth;
            const y = plotY + row * cellHeight;
            const key = `${rowLabels[row]}-${columnLabels[column]}-${row}-${column}`;
            const value = grid[row]?.[column] ?? null;
            if (fraction === null || value === null) {
              return (
                <g key={key}>
                  <rect
                    fill={MISSING_FILL}
                    height={px(cellHeight)}
                    stroke={CHART_INK.background}
                    strokeWidth={1}
                    width={px(cellWidth)}
                    x={px(x)}
                    y={px(y)}
                  />
                  <line
                    stroke={MISSING_LINE}
                    strokeWidth={1}
                    x1={px(x + 3)}
                    x2={px(x + cellWidth - 3)}
                    y1={px(y + cellHeight - 3)}
                    y2={px(y + 3)}
                  />
                </g>
              );
            }
            const fill = fillAt(fraction);
            const text = valueText(fmt, value);
            return (
              <g key={key}>
                <rect
                  fill={fill}
                  height={px(cellHeight)}
                  stroke={CHART_INK.background}
                  strokeWidth={1}
                  width={px(cellWidth)}
                  x={px(x)}
                  y={px(y)}
                />
                {showValues ? (
                  <text
                    fill={textColorOn(fill)}
                    fontSize={VALUE_SIZE}
                    textAnchor="middle"
                    x={px(x + cellWidth / 2)}
                    y={px(y + cellHeight / 2 + VALUE_SIZE * 0.35)}
                  >
                    {text}
                  </text>
                ) : null}
              </g>
            );
          })
        )}
      </g>
      <ColorBar
        by={by}
        height={barHeight}
        highText={highText}
        lowText={lowText}
        palette={palette}
        reverse={reverse}
        scale={scale}
        tick={tick}
        x={barX}
        y={barY}
      />
    </ChartSvg>
  );
}
